/** @file Vertex drag gesture: a moved-wire preview every move, one setVertices or deform key on release. */
import { meshOffsets } from '@pose2d/commands/animation/meshOffsets';
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import type { CommandCall, DeformKey } from '@pose2d/format/types';
import { OVERLAY_STYLE } from '@pose2d/overlay/overlayStyle';
import type { ToolContext, ToolGesture, ViewportNotice, ViewportPointer } from '../../panels/viewport/viewportTypes';
import { selectionStore } from '../../stores/selectionStore';
import { deformAtFrame } from './deformAtFrame';
import { dragVertices, type VertexDragStart } from './dragVertices';
import { editMeshStore } from './editMeshStore';
import { followUvs } from './followUvs';
import { meshGizmo } from './meshGizmo';
import type { MeshScene } from './meshScene';
import { pendingOf } from './pendingOf';
import { selectVertices } from './selectVertices';
import { vertexEditCalls, type VertexEdit } from './vertexEditCalls';
import { vertexFrames } from './vertexFrames';
import { vertexSelection } from './vertexSelection';

/** Animate mode needs an animation, and a deform key at `frame` unless auto-key is on (R-E5-04). */
function refusal(ctx: ToolContext, keys: readonly DeformKey[] | undefined, frame: number): ViewportNotice | null {
  if (ctx.tool.mode !== 'animate') return null;
  if (ctx.playhead.animation === null) return { kind: 'info', key: 'tools.editMesh.pickAnimation' };
  const keyed = ctx.playhead.autoKey || (keys ?? []).some((k) => k.f === frame);
  return keyed ? null : { kind: 'info', key: 'tools.editMesh.autoKeyOff' };
}

/** The selected vertices of the mesh, or `hit` alone (and selected) when the grabbed vertex is not among them. */
function grab(s: MeshScene, hit: number): number[] {
  const indices = vertexSelection.of(selectionStore.getState(), s.target.attachment, s.target.mesh.uvs.length / 2);
  if (indices.includes(hit)) return indices;
  selectVertices(s.target.attachment, [hit], 'replace');
  return [hit];
}

/** World points with `moved` shifted by (dx, dy): where the drag draws the wire. */
function shifted(points: Float32Array, moved: readonly number[], dx: number, dy: number): Float32Array {
  const out = Float32Array.from(points);
  for (const v of moved) {
    out[2 * v] = (out[2 * v] ?? 0) + dx;
    out[2 * v + 1] = (out[2 * v + 1] ?? 0) + dy;
  }
  return out;
}

/** The drag of vertex `hit` pressed at `press`, or null (with a notice) when Animate mode refuses it. */
export function vertexDragGesture(s: MeshScene, hit: number, press: ViewportPointer): ToolGesture | null {
  const { ctx, target } = s;
  const keys = ctx.playhead.animation === null ? undefined : ctx.doc.animations[ctx.playhead.animation]?.deform[target.attachment];
  // W3-31: the playhead is fractional while playing; round once at press for sampling, the key check and the key.
  const frame = Math.max(0, Math.round(ctx.playhead.frame));
  const refused = refusal(ctx, keys, frame);
  if (refused !== null) {
    ctx.notify(refused);
    return null;
  }
  const indices = grab(s, hit);
  const rest = meshOffsets.rest(target.mesh);
  const base = ctx.tool.mode === 'animate' ? deformAtFrame(keys, frame, rest.length) : rest;
  const start: VertexDragStart = { indices, anchor: hit, world: s.points, frames: vertexFrames.of(ctx.doc, ctx.data, ctx.pose, target, s.at), base };
  const edit: VertexEdit = { attachment: target.attachment, mode: ctx.tool.mode, animation: ctx.playhead.animation, frame };
  const image = ctx.doc.images[target.mesh.image];
  const follow = ctx.tool.mode === 'setup' && editMeshStore.getState().keepImage && image !== undefined ? image : null;
  const from = viewCamera.screenToWorld(ctx.camera, press.sx, press.sy, [0, 0]);
  const picked = new Set(indices);
  let calls: CommandCall[] = [];
  return {
    move(p) {
      const to = viewCamera.screenToWorld(ctx.camera, p.sx, p.sy, [0, 0]);
      const r = dragVertices(start, { dx: to[0] - from[0], dy: to[1] - from[1], keys: { ctrl: p.ctrl, shift: p.shift, alt: p.alt }, snap: ctx.snap, grid: ctx.grid });
      calls = vertexEditCalls(edit, r.values, follow === null ? null : followUvs(target.mesh, follow, base, r.values, indices));
      ctx.setGizmo(meshGizmo(ctx.camera, shifted(s.points, indices, r.dx, r.dy), target.mesh.triangles, (v) => (picked.has(v) ? OVERLAY_STYLE.selected : OVERLAY_STYLE.vertex)));
    },
    release: () => pendingOf(calls),
    cancel: () => undefined,
  };
}
